import { propostaEstaAberta } from '@modules/aawz-intelligence-v2/utils/proposta-aberta'
import {
  desfechoDaProposta,
  linhasDoRelatorioDoLote,
} from '@modules/aawz-intelligence-v2/utils/desfecho-da-proposta'
import { PROPOSTAS_LOTE_TEXT as TEXTO } from '@modules/aawz-intelligence-v2/constants/Text.propostas-lote'

// ★ "Aprovar todas" — decidir N propostas do Modo Agente num gesto só.
//
// Pedido do owner (16/09/2026): ele viu 3 propostas abertas e quer decidir de
// uma vez, "dando escala".
//
// **Não existe rota de lote.** Cada decisão continua sendo um
// `POST /runs/{id_run}/propostas/{id_proposta}/decisao` próprio, com
// `decisao`, `hash_payload` e um `decision_id` NOVO — quem repete a chamada é
// este mixin, reusando `decidirProposta` do `view-chat-modo-agente.mixin.js`
// (fonte única do contrato da rota, do `decision_id` e da escrita no mapa).
//
// Arquivo separado do mixin do Modo Agente porque aquele está em 403 linhas e
// o teto do repo é 500 — e porque este aqui tem uma responsabilidade própria:
// orquestrar N decisões e contar o que gravou.
//
// Testes: `view-chat-lote.unit.js` (série, os 3 campos por chamada, falha
// parcial, não-interrupção, idempotência do retry, filtro de abertas, vazio e
// o passo de confirmação).
export const viewChatLoteMixin = {
  data() {
    return {
      // Trava de reentrância do GESTO: o laço é longo (N POSTs em série) e um
      // segundo clique no meio dele mandaria uma segunda decisão para as
      // propostas que ainda não saíram. Também apaga o botão durante o laço.
      aprovandoEmLote: false,
      // [spec-003 T302 / US-8] Houve um gesto de lote NESTE run.
      //
      // ★ O relatório é "o que foi e o que NÃO foi feito no LOTE" — mostrá-lo
      // depois de uma decisão individual chamaria de lote um gesto que não foi.
      // Zera sozinho quando o mapa esvazia (troca de conversa, run novo — ver
      // o watcher abaixo).
      houveLoteDecidido: false,
    }
  },
  watch: {
    // `resetPropostasAbertas` é despachado na troca de conversa e no início de
    // cada run (`aceitarRun`). Sem zerar aqui, o relatório do lote ANTERIOR
    // reapareceria no primeiro desfecho do run seguinte.
    propostasAbertas: {
      deep: true,
      handler(mapa) {
        if (!mapa || Object.keys(mapa).length === 0) this.houveLoteDecidido = false
      },
    },
  },
  computed: {
    // A lista que o lote decide — a MESMA regra do contador do chip
    // (`propostaEstaAberta`, que também descarta a expirada pelo relógio
    // local). Sem isso, "aprovar todas" mandaria decisão para proposta já
    // resolvida e o backend responderia erro numa operação que o consultor
    // nunca pediu.
    propostasAbertasParaLote() {
      return Object.values(this.propostasAbertas).filter(propostaEstaAberta)
    },
    // O botão só aparece a partir de DUAS: com uma proposta aberta o cartão
    // dela já tem "Aprovar" à vista, e um segundo botão no cabeçalho com o
    // mesmo efeito confundiria mais do que economizaria clique.
    mostrarAprovarTodas() {
      return this.propostasAbertasParaLote.length > 1
    },
    /**
     * [spec-003 T302 / US-8] O relatório por ação: estado, recibo quando houver
     * e motivo quando não houver.
     *
     * Sai do MAPA de propostas, que é onde o desfecho de cada uma já mora — o
     * `resumo` que `aprovarTodasAsPropostas` devolve conta só "gravou/não
     * gravou" da CHAMADA, e não distingue a negada da expirada. A tradução dos
     * cinco `resultado` do contrato para os quatro desfechos da US-8 é pura e
     * vive em `utils/desfecho-da-proposta.js`.
     *
     * ⚠️ Proposta ainda ABERTA fica de fora — é o que continua na mesa.
     *
     * [T313] O relatório SOBREVIVE ao F5: `GET /conversas/{id}/run-atual`
     * passou a devolver `propostas` (a trilha de `run_decisoes`, v661), que
     * `_aplicarRelatorioDoRun` reidrata neste mesmo mapa.
     */
    linhasDoRelatorioDoLote() {
      return linhasDoRelatorioDoLote(this.propostasAbertas)
    },
    /**
     * [T313 / US-8 AC3] O relatório veio da trilha PERSISTIDA do run, e não
     * de um gesto desta sessão.
     *
     * `houveLoteDecidido` é `data`: nasce `false` a cada montagem, então uma
     * aba recarregada não tem como saber que houve gesto — era exatamente por
     * isso que o relatório sumia no F5. Quem responde por ele depois do
     * reload é a ORIGEM de cada linha (`reidratada`, carimbada por
     * `propostaDoRelatorio`).
     *
     * ★ Exige `desfechoDaProposta` além do carimbo: um run reidratado em que
     * NADA foi decidido ainda é "o que continua na mesa", e cada proposta tem
     * o cartão dela logo acima — abrir um relatório ali anunciaria um
     * desfecho que não existe.
     */
    houveRelatorioPersistido() {
      const mapa = this.propostasAbertas || {}
      return Object.keys(mapa).some(function (id) {
        const proposta = mapa[id]
        return !!(proposta && proposta.reidratada && desfechoDaProposta(proposta))
      })
    },
    mostrarRelatorioDoLote() {
      const temFonte = this.houveLoteDecidido || this.houveRelatorioPersistido
      return temFonte && this.linhasDoRelatorioDoLote.length > 0
    },
  },
  methods: {
    /**
     * O clique no chip: CONFIRMA antes de gravar, listando o que será gravado.
     *
     * Aprovar N propostas é autorizar N escritas irreversíveis de uma vez. A
     * lista (não só a contagem) é o que permite reconhecer o que está sendo
     * autorizado — e é barata, porque o `preview` de cada proposta já está no
     * mapa desde o `tool-approval-request`.
     */
    onAprovarTodas() {
      const abertas = this.propostasAbertasParaLote
      if (!abertas.length || this.aprovandoEmLote) return
      this.$showModal({
        v2: true,
        center: true,
        title: TEXTO.titulo(abertas.length),
        text: TEXTO.corpo(abertas.map(this.resumoDaProposta)),
        buttons: [
          { text: TEXTO.botaoCancelar },
          { text: TEXTO.botaoConfirmar, action: () => this.aprovarTodasAsPropostas() },
        ],
      })
    },
    resumoDaProposta(proposta) {
      const preview = proposta.preview || {}
      return preview.resumo || TEXTO.itemSemResumo(proposta.id_proposta)
    },
    /**
     * Decide todas as abertas, uma POR VEZ.
     *
     * **Série, nunca `Promise.all`:** cada decisão pode disparar a continuação
     * do run no worker; três de uma vez é pedir corrida no backend.
     *
     * **Falha parcial é o caso normal, não a exceção.** Não há transação
     * atravessando N POSTs: se a 2ª falha, a 1ª JÁ gravou. Por isso o laço não
     * para na falha — interromper deixaria as seguintes sem tentativa, sem
     * ninguém ter decidido isso — e o retorno diz, por proposta, o que gravou
     * e o que não. Quem falhou fica com `erro` na própria entrada do mapa, que
     * é o que acende o "Tentar novamente" do cartão (reusando o MESMO
     * `decision_id`, replay idempotente pelo contrato).
     *
     * ⚠️ O desfecho de cada cartão vem do RECIBO da decisão, não do turno
     * seguir: a retomada após a aprovação está quebrada no worker (A-118), e
     * um lote que dependesse dela pareceria ter falhado com as três gravadas.
     *
     * @returns {Promise<{gravadas: string[], falhas: Array<{id_proposta: string, motivo: string}>}>}
     */
    async aprovarTodasAsPropostas() {
      const resumo = { gravadas: [], falhas: [] }
      const abertas = this.propostasAbertasParaLote
      if (!abertas.length || this.aprovandoEmLote) return resumo
      this.aprovandoEmLote = true
      try {
        for (const proposta of abertas) {
          // O `await` DENTRO do laço é o ponto: é ele que faz a série.
          // eslint-disable-next-line no-await-in-loop
          const desfecho = await this.decidirProposta(proposta, 'aprovar', null, true)
          this.anotarDesfecho(resumo, proposta, desfecho)
        }
      } finally {
        // `finally`: uma exceção inesperada no meio não pode deixar o botão
        // desabilitado para sempre.
        this.aprovandoEmLote = false
        // [T302] Dentro do `finally`: uma exceção no meio do laço deixa
        // algumas propostas JÁ gravadas, e é justamente aí que o gestor mais
        // precisa ver o que aconteceu com cada uma.
        this.houveLoteDecidido = true
      }
      this.avisarDesfechoDoLote(resumo)
      return resumo
    },
    anotarDesfecho(resumo, proposta, desfecho) {
      if (desfecho && desfecho.ok) {
        resumo.gravadas.push(proposta.id_proposta)
        return
      }
      resumo.falhas.push({
        id_proposta: proposta.id_proposta,
        motivo: (desfecho && desfecho.motivo) || '',
      })
    },
    /**
     * UM aviso por gesto, dizendo o que gravou e o que não.
     *
     * Três snacks para um clique só seria ruído; e "3 aprovadas" quando uma
     * falhou seria pior — o consultor sairia achando que o registro existe.
     * Por isso `decidirProposta` é chamada em modo silencioso aqui: o snack
     * individual dela é do clique individual.
     */
    avisarDesfechoDoLote(resumo) {
      const gravadas = resumo.gravadas.length
      const falhas = resumo.falhas.length
      if (falhas && !gravadas) {
        this.$showSnack({ text: TEXTO.todasFalharam(falhas), color: 'error', button: true })
        return
      }
      if (falhas) {
        this.$showSnack({ text: TEXTO.parcial(gravadas, falhas), color: 'warning', button: true })
        return
      }
      this.$showSnack({ text: TEXTO.todasGravadas(gravadas), color: 'success', button: true })
    },
  },
}
